import React from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { Building, Cake, Heart, ArrowRight, Mail, Phone, Sparkles } from "lucide-react";
import { useSEO } from "../lib/useSEO";
import { PageDecor } from "../components/decor/PageDecor";
import NotFound from "./NotFound";
import { thumb, fallbackToFull } from "../lib/imageThumb";

const SERVICES = [
  {
    slug: "location-salle",
    icon: Building, title: "Location de salle", pricing: "CHF 60.– / heure",
    extra: "Tarif dégressif dès plusieurs heures",
    desc: "Salle équipée pour danse ou activités artistiques. Sono professionnelle et éclairage LED multicolore inclus. Réservations possibles en semaine, le week-end et pendant les vacances scolaires.",
    image: "/img/a6fa6ffb7b.webp",
    features: ["Sono pro & éclairage inclus", "Studio bois & miroirs", "Tarif dégressif", "Réservation flexible"],
    testid: "service-location",
    seo_title: "Location de salle équipée — 1000 ARTS Cugy VD",
    seo_desc: "Loue notre studio équipé (sono pro, éclairage LED, miroirs) dès CHF 60/h à Cugy VD. Réservation flexible en semaine, week-end et vacances scolaires.",
  },
  {
    slug: "anniversaires",
    icon: Cake, title: "Fêtes d'anniversaire", pricing: "CHF 170.– / heure",
    extra: "1 à 8 enfants · CHF 10.– par enfant supp. · CHF 120.– / heure supp.",
    desc: "On s'occupe de tout : animation, choré, ambiance et fun. Tu amènes les enfants et le gâteau, on transforme leur après-midi en mémoire inoubliable.",
    image: "/img/232a5f0e06.webp",
    features: ["Animation incluse", "Choré sur mesure", "Ambiance & musique", "Tarif heure supp. à confirmer"],
    testid: "service-anniversaire",
    seo_title: "Anniversaires enfants clés en main — 1000 ARTS Cugy VD",
    seo_desc: "Organise un anniversaire dansé inoubliable dès CHF 170. Animation, chorégraphie et ambiance incluses. 1 à 8 enfants, sans stress pour les parents.",
  },
  {
    slug: "mariage",
    icon: Heart, title: "Danse pour mariage", pricing: "Sur devis",
    extra: "Flashmob · Ouverture de bal · EVJF & EVG",
    desc: "Préparation de flashmob, danse d'ouverture de bal, animation d'enterrement de vie de jeune fille ou jeune homme. On accompagne la création d'un moment dansé personnalisé.",
    image: "https://images.unsplash.com/photo-1519741497674-611481863552?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    features: ["Flashmob de groupe", "Ouverture de bal", "EVJF · EVG", "Choré personnalisée"],
    testid: "service-mariage",
    seo_title: "Danse de mariage & flashmob — 1000 ARTS Cugy VD",
    seo_desc: "Ouverture de bal, flashmob, EVJF/EVG : on prépare avec toi la chorégraphie qui rendra ton mariage inoubliable. Devis personnalisé sous 48h.",
  },
];

export default function Prestations() {
  const location = useLocation();
  const { slug } = useParams();
  const activeService = slug ? SERVICES.find((s) => s.slug === slug) : null;
  const services = activeService ? [activeService] : SERVICES;

  // Un slug de prestation inconnu ne doit pas afficher la page d'aperçu comme si elle
  // était la page demandée : on renvoie un 404 non indexable.
  const unknownSlug = Boolean(slug) && !activeService;
  const canonical = activeService ? `/evenements/${activeService.slug}` : "/evenements";
  useSEO({
    title: activeService ? activeService.seo_title : "Événements — Anniversaires, mariages, location de salle · 1000 ARTS Cugy VD",
    description: activeService ? activeService.seo_desc : "1000 ARTS met à ta disposition ses salles, anime tes fêtes d'anniversaire, prépare ta première danse de mariage et crée des flashmobs sur mesure.",
    canonical: unknownSlug ? undefined : canonical,
    robots: unknownSlug ? "noindex, follow" : undefined,
  });

  if (unknownSlug) return <NotFound />;

  return (
    <div data-testid="page-prestations" className="pt-32 pb-24 relative overflow-hidden mesh-bg">
      <PageDecor />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10">
      <div className="eyebrow mb-3">Au-delà des cours</div>
      {activeService ? (
        <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-tight">
          <span style={{ color: "var(--primary)" }}>{activeService.title}</span>
        </h1>
      ) : (
        <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-tight">
          Événements &amp;<br /><span style={{ color: "var(--primary)" }}>prestations</span>
        </h1>
      )}
      <p className="mt-6 text-lg text-[var(--muted)] max-w-2xl">
        {activeService
          ? activeService.desc
          : "Location de salle, animation d'anniversaires, préparation de mariage, flashmob sur mesure — 1000 ARTS, c'est aussi un studio événementiel au cœur du Gros-de-Vaud."}
      </p>

      <div className="mt-16 space-y-10">
        {!activeService && (
          /* Les workshops heels ne sont pas une prestation sur devis : ils ont leur page
             et leur formulaire d'inscription. On les annonce ici pour qu'on les trouve. */
          <article data-testid="service-workshops-heels" className="card-flat p-8 md:p-12">
            <div className="eyebrow">Tous les mois · Samedi après-midi</div>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight mt-2">
              Workshops heels technique &amp; freestyle
            </h2>
            <p className="text-[var(--muted)] mt-4 leading-relaxed max-w-2xl">
              Un samedi par mois, 1h30 de heels pour tous les niveaux : la technique
              d'abord, le freestyle ensuite. Les séances se prennent à l'unité ou au
              forfait du semestre, et le dernier workshop se termine par une vidéo.
            </p>
            <Link to="/evenements/workshops-heels" className="btn-primary mt-6 inline-flex"
              data-testid="lien-workshops-heels">
              Voir les dates et m'inscrire <ArrowRight size={16} />
            </Link>
          </article>
        )}
        {services.map((s, idx) => (
          <article key={s.title} data-testid={s.testid} className="grid lg:grid-cols-2 gap-6 items-stretch card-flat" style={{ padding: 0, overflow: "hidden" }}>
            <div className={`aspect-[4/3] lg:aspect-auto lg:min-h-[380px] overflow-hidden ${idx % 2 === 1 ? "lg:order-2" : ""}`}>
              <img loading="lazy" decoding="async" src={thumb(s.image)} onError={fallbackToFull(s.image)} alt={s.title} className="w-full h-full object-cover" />
            </div>
            <div className="p-8 md:p-12 flex flex-col justify-center">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4" style={{ background: "var(--primary-tint)" }}>
                <s.icon size={26} style={{ color: "var(--primary)" }} />
              </div>
              <h2 className="font-display text-3xl md:text-4xl tracking-tight">{s.title}</h2>
              <p className="text-[var(--muted)] mt-4 leading-relaxed">{s.desc}</p>
              <ul className="mt-5 grid grid-cols-2 gap-2 text-sm text-[var(--muted)]">
                {s.features.map((f) => <li key={f} className="flex items-center gap-2"><Sparkles size={12} style={{ color: "var(--primary)" }} /> {f}</li>)}
              </ul>
              <div className="mt-6 pt-5 border-t border-[var(--border-soft)]">
                <div className="font-display text-3xl" style={{ color: "var(--primary)" }}>{s.pricing}</div>
                <div className="text-xs text-[var(--muted)] mt-1">{s.extra}</div>
              </div>
              <Link to="/contact" data-testid={`${s.testid}-cta`} className="btn-primary mt-6 self-start">Demander un devis <ArrowRight size={16} /></Link>
            </div>
          </article>
        ))}
      </div>

      {/* Lien vers autres prestations si on est sur une sous-page */}
      {activeService && (
        <section className="mt-16" data-testid="other-services">
          <div className="eyebrow mb-3">Nos autres prestations</div>
          <div className="grid md:grid-cols-2 gap-4">
            {SERVICES.filter((s) => s.slug !== activeService.slug).map((s) => (
              <Link
                key={s.slug}
                to={`/evenements/${s.slug}`}
                data-testid={`other-service-${s.slug}`}
                className="card-flat flex items-center gap-4 hover:shadow-lg transition-all"
                style={{ padding: 20 }}
              >
                <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--primary-tint)" }}>
                  <s.icon size={22} style={{ color: "var(--primary)" }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg leading-tight">{s.title}</div>
                  <div className="text-xs text-[var(--muted)] mt-1">{s.extra}</div>
                </div>
                <ArrowRight size={18} style={{ color: "var(--primary)" }} />
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="mt-20 card-flat grid md:grid-cols-3 gap-8 items-center" data-testid="prestations-cta-bar">
        <div className="md:col-span-2">
          <div className="eyebrow mb-3">Un projet en tête ?</div>
          <h3 className="font-display text-3xl md:text-4xl tracking-tight">Parle-nous de ton événement</h3>
          <p className="text-[var(--muted)] mt-3">On revient vers toi sous 48h avec une proposition sur mesure.</p>
        </div>
        <div className="flex flex-col gap-3">
          <a href="mailto:info1000arts@gmail.com" data-testid="prestations-email" className="btn-primary justify-center"><Mail size={16} /> Nous écrire</a>
          <a href="tel:+41799504031" data-testid="prestations-phone" className="btn-outline justify-center"><Phone size={16} /> 079 950 40 31</a>
        </div>
      </section>
      </div>
    </div>
  );
}